import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAtom } from "jotai";
import { SeIcon } from "@se/web-ui-react";
import "./MGTariff.scss";
import moment from "moment";
import "react-datepicker/dist/react-datepicker.css";
import TimeSlotsDisplay from "./TimeSlotDisplay";
import WeekdaysSelector from "./WeekDaySelector";
import DynamicTabbar from "./TabBar";
import {
  generateTimeOptions,
  formatTime,
  getUsedWeekdays,
  WEEK_DAYS,
  getRandomInt,
} from "./TarifUtils";
import TariffDataSheet from "./TariffDataSheet";
import { DateInput } from "@mantine/dates";
import { IconCalendar } from "@tabler/icons-react";
import {
  Button,
  useMantineColorScheme,
  Group,
  Checkbox,
  TextInput,
  Select,
} from "@mantine/core";
import PropTypes from "prop-types";
import { useDualTranslation } from "../utils/i18nHelpers";
import { useProjectDateFormat } from "../utils/helpers";
import { componentStatusAtom } from "../utils/atoms/componentStatus";
import SEConfirmModal from "../components/common/SEConfirmModal";
// import SEConfirmModal from "../../components/common/SEConfirmModal";

const DECIMAL_INPUT_REGEX = /^\d{0,12}(\.\d{0,4})?$/; // length-limited to avoid regex hotspot

const now = new Date();
const randomStartDate = new Date(now.getFullYear() - 1, 0, 1);

const prevYear = now.getFullYear() - 1;
const pad = (n) => n.toString().padStart(2, '0');
const seasonData = [
  {
    seasonId: getRandomInt(1, 9999),
    name: "Season 1",
    startDay: `01-01-${prevYear}`,
    endDay: `31-12-${prevYear}`,
    isBaseCharge: false,
    baseCharge: "",

    seasonStartTime: {
      hour: 0,
      minute: 0,
    },
    seasonEndTime: {
      hour: 24,
      minute: 0,
    },
    rateMatrix: [],
    holidays: false,
    periods: [
      {
        dayGroupId: 7340,
        dayGroupName: "WeekDays",
        weekDays: [1, 2, 3, 4, 5],
        rate: [
          {
            tariffId: 7864,
            name: "PERIOD 1",
            demandType: "MonthlyMax_US",
            demandCost: 0,
            minPowerValue: 0,
            maxPowerValue: "Infinity",
            timeSlots: [
              {
                startTime: {
                  hour: 0,
                  minute: 0,
                },
                endTime: {
                  hour: 24,
                  minute: 0,
                },
              },
            ],
            options: {
              tierType: "Off",
              tierBoundaries: [
                {
                  tierId: 2904,
                  price: "0.1",
                  consumption: "1",
                },
              ],
            },
          },
        ],
      },
      {
        dayGroupId: 3100,
        dayGroupName: "WeekEnd",
        rate: [
          {
            tariffId: 3376,
            name: "PERIOD 1",
            demandType: "MonthlyMax_US",
            demandCost: 0,
            minPowerValue: 0,
            maxPowerValue: "Infinity",
            timeSlots: [
              {
                startTime: {
                  hour: 0,
                  minute: 0,
                },
                endTime: {
                  hour: 24,
                  minute: 0,
                },
              },
            ],
            options: {
              tierType: "Off",
              tierBoundaries: [
                {
                  tierId: 2953,
                  price: "0.1",
                  consumption: "1",
                },
              ],
            },
          },
        ],
        weekDays: [6, 7],
      },
    ],
  },
];

const withDefaultPrice = (seasons, price) =>
  seasons.map((season) => ({
    ...season,
    periods: season.periods.map((period) => ({
      ...period,
      rate: period.rate.map((rate) => ({
        ...rate,
        options: {
          ...rate.options,
          tierBoundaries: rate.options.tierBoundaries.map((tier) => ({ ...tier, price })),
        },
      })),
    })),
  }));

export const MGTariff = forwardRef((props, ref) => {
  const { t } = useDualTranslation();
  const projectDateFormat = useProjectDateFormat();
  const defaultEnergyPrice = props.isExport ? 0 : 0.1;
  // Deep-clone incoming template or fallback seasonData to avoid shared references across instances
  const initialTemplate = props.tariffTemplate
    ? JSON.parse(JSON.stringify(props.tariffTemplate))
    : props.isExport
    ? withDefaultPrice(seasonData, defaultEnergyPrice)
    : JSON.parse(JSON.stringify(seasonData));
  // Unique snackbar id per component instance
  const snackbarIdRef = useRef(`snackbar-${getRandomInt(1, 999999)}`);
  const [seasonsList, setSeasonsList] = useState(initialTemplate);
  const [activeSeason, setActiveSeason] = useState(0);
  const [editingIndex, setEditingIndex] = useState(null);
  const [newItemValue, setNewItemValue] = useState("");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedSeason, setSelectedSeason] = useState(
    initialTemplate?.[0] ? JSON.parse(JSON.stringify(initialTemplate[0])) : null
  );
  const [isBaseCharge, setIsBaseCharge] = useState(!!initialTemplate?.[0]?.isBaseCharge);
  const [baseCharge, setBaseCharge] = useState(initialTemplate?.[0]?.baseCharge || "");
  const [calculatedEndDate, setCalculatedEndDate] = useState(
    moment(randomStartDate).add(1, "years").subtract(1, "days").toDate()
  );
  const [snackbarProps, setSnackbarProps] = useState({
    open: false,
    type: "success",
    canClose: true,
    message: t("common.success"),
    icon: "information_circle",
    button: t("common.close"),
    duration: 5000,
  });

  const [activeDayGroup, setActiveDayGroup] = useState(0);
  const [editingDayGroupIndex, setEditingDayGroupIndex] = useState(null);
  const [newDayGroupValue, setNewDayGroupValue] = useState("");
  const [, setComponentStatus] = useAtom(componentStatusAtom);
  
  // Confirmation modal states
  const [showConfirmRemoveSeason, setShowConfirmRemoveSeason] = useState(false);
  const [seasonToRemove, setSeasonToRemove] = useState(null);
  const [showConfirmRemoveDayGroup, setShowConfirmRemoveDayGroup] = useState(false);
  const [dayGroupToRemove, setDayGroupToRemove] = useState(null);
  const [copyFromEnabled, setCopyFromEnabled] = useState(false);
  const [copyFromSeasonId, setCopyFromSeasonId] = useState(null);
  const [showCopyConfirmModal, setShowCopyConfirmModal] = useState(false);
  const [pendingCopySeasonId, setPendingCopySeasonId] = useState(null);
  const isHydratingRef = useRef(true);
  const isReadOnly = props.isVersionCompleted;

  const syncBaseChargeFromSeason = (season) => {
    setIsBaseCharge(!!season?.isBaseCharge);
    setBaseCharge(season?.baseCharge || "");
  };

  useEffect(() => {
    if (props.tariffTemplate) {
      isHydratingRef.current = true;
      const cloned = JSON.parse(JSON.stringify(props.tariffTemplate));
      setSeasonsList(cloned);
      setActiveSeason(0);
      setActiveDayGroup(0);
      setSelectedSeason(cloned?.[0] ? JSON.parse(JSON.stringify(cloned[0])) : null);
      syncBaseChargeFromSeason(cloned?.[0]);
    }
  }, [props.tariffTemplate]);

  useEffect(() => {
    if (isHydratingRef.current) {
      isHydratingRef.current = false;
      return;
    }
    // setComponentStatus((prev) => ({
    //   ...prev,
    //   grid: { isEditing: true, completed: false },
    // }));
  }, [seasonsList, setComponentStatus]);

  useEffect(() => {
    setSelectedSeason((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        isBaseCharge,
        baseCharge: isBaseCharge ? baseCharge : undefined,
      };
    });
  }, [isBaseCharge, baseCharge]);

  const timeOptions = generateTimeOptions();
  useImperativeHandle(ref, () => ({
    getJsonData: () => {
      return seasonsList;
    },
  }));
  const isFirstSeason = useCallback(() => {
    return seasonsList.length > 0 && seasonsList[0].seasonId === selectedSeason.seasonId;
  }, [selectedSeason, seasonsList]);
  const callSnackbar = (type, message) => {
    setSnackbarProps({
      type,
      message,
    });
    const el = document.getElementById(snackbarIdRef.current);
    if (el) el.open = true;
  };

  const addSeason = () => {
    if (isReadOnly) return;
    props?.onChange?.();
    // Check previous season's start and end day
    if (seasonsList.length > 0) {
      const prevSeason = seasonsList[seasonsList.length - 1];
      if (prevSeason.startDay === prevSeason.endDay) {
        callSnackbar(
          "error",
          t("tariff.error.prevSeasonSameDay")
        );
        return;
      }
    }

    if (
      !isFirstSeason() &&
      moment(calculatedEndDate).isSame(moment(selectedSeason.startDay))
    ) {
      callSnackbar(
        "error",
        t("tariff.error.sameStartDate")
      );
      return;
    }
    // New rule: the new season's start date should be the previous season's end date (shared boundary day).
    // If there is no previous season, fall back to calculatedEndDate logic.
    const pad = (n) => n.toString().padStart(2, "0");
    let newSeasonStartDate;
    if (seasonsList.length > 0) {
      // Use previous season's endDay directly (do not modify previous season).
      newSeasonStartDate = seasonsList[seasonsList.length - 1].endDay; // already in dd-mm-yyyy format
    } else {
      const year = calculatedEndDate.getFullYear();
      const month = pad(calculatedEndDate.getMonth() + 1);
      const day = pad(calculatedEndDate.getDate());
      newSeasonStartDate = `${day}-${month}-${year}`;
    }
    
    // Validate new season falls within 1-year window from Season 1's start date
    if (seasonsList.length > 0) {
      const [nsd, nsm, nsy] = newSeasonStartDate.split('-');
      const newStartDate = new Date(`${nsy}-${nsm}-${nsd}`);
      
      const [fd, fm, fy] = seasonsList[0].startDay.split('-');
      const firstSeasonStart = new Date(`${fy}-${fm}-${fd}`);
      const windowEnd = moment(firstSeasonStart).add(1, 'years').subtract(1, 'days').toDate();
      
      // if (moment(newStartDate).isAfter(windowEnd)) {
      //   callSnackbar("error", t("tariff.error.seasonOutsideYearWindow") || "New season start date must fall within 1 year from the first season start date");
      //   return;
      // }
    }
    
    // Initialize end date same as start; user will adjust later.
    const newSeasonEndDate = newSeasonStartDate;

    const nextSeasonNumber = seasonsList.length + 1;
    const newSeason1 = {
      seasonId: getRandomInt(1, 9999),
      name: `Season ${nextSeasonNumber}`,
      startDay: newSeasonStartDate,
      endDay: newSeasonEndDate,
      isBaseCharge: false,
      baseCharge: "",
      seasonStartTime: {
        hour: 0,
        minute: 0,
      },
      seasonEndTime: {
        hour: 24,
        minute: 0,
      },
      rateMatrix: [],
      holidays: false,
      periods: [
        {
          dayGroupId: 7340,
          dayGroupName: "WeekDays",
          weekDays: [1, 2, 3, 4, 5],
          rate: [
            {
              tariffId: 7864,
              name: "PERIOD 1",
              demandType: "MonthlyMax_US",
              demandCost: 0,
              minPowerValue: 0,
              maxPowerValue: "Infinity",
              timeSlots: [
                {
                  startTime: {
                    hour: 0,
                    minute: 0,
                  },
                  endTime: {
                    hour: 24,
                    minute: 0,
                  },
                },
              ],
              options: {
                tierType: "Off",
                tierBoundaries: [
                  {
                    tierId: 2904,
                    price: defaultEnergyPrice,
                    consumption: 0,
                  },
                ],
              },
            },
          ],
        },
        {
          dayGroupId: 3100,
          dayGroupName: "WeekEnd",
          rate: [
            {
              tariffId: 3376,
              name: "PERIOD 1",
              demandType: "MonthlyMax_US",
              demandCost: 0,
              minPowerValue: 0,
              maxPowerValue: "Infinity",
              timeSlots: [
                {
                  startTime: {
                    hour: 0,
                    minute: 0,
                  },
                  endTime: {
                    hour: 24,
                    minute: 0,
                  },
                },
              ],
              options: {
                tierType: "Off",
                tierBoundaries: [
                  {
                    tierId: 2953,
                    price: defaultEnergyPrice,
                    consumption: "2",
                  },
                ],
              },
            },
          ],
          weekDays: [6, 7],
        },
      ],
    };

    setSeasonsList((prevSeasonsList) => {
      let updatedSeasonsList = [...prevSeasonsList];
      // Do NOT adjust previous season endDay; seasons now share boundary day as requested.
      updatedSeasonsList = [...updatedSeasonsList, newSeason1];
      setActiveSeason(updatedSeasonsList.length - 1);
      setActiveDayGroup(0);
      setSelectedSeason(updatedSeasonsList[updatedSeasonsList.length - 1]);
      syncBaseChargeFromSeason(newSeason1);
      return updatedSeasonsList;
    });
  };

  const handleSeasonClick = (index) => {
    setActiveSeason(index);
    setSelectedSeason(seasonsList[index]);
    setActiveDayGroup(0);
    syncBaseChargeFromSeason(seasonsList[index]);
  };

  const removeSeason = (seasonId) => {
    if (isReadOnly) return;
    setSeasonToRemove(seasonId);
    setShowConfirmRemoveSeason(true);
  };

  const confirmRemoveSeason = () => {
    if (!seasonToRemove) return;
    
    setSeasonsList((prevSeasonsList) => {
      const removedSeasonIndex = prevSeasonsList.findIndex(
        (season) => season.seasonId === seasonToRemove.seasonId
      );
      let updatedSeasonsList = prevSeasonsList.filter(
        (season) => season.seasonId !== seasonToRemove.seasonId
      );

      if (
        removedSeasonIndex > 0 &&
        removedSeasonIndex < prevSeasonsList.length - 1
      ) {
        const previousSeasonIndex = removedSeasonIndex - 1;
        const nextSeasonAfterRemoved = updatedSeasonsList[removedSeasonIndex];

        if (nextSeasonAfterRemoved?.startDay) {
          const updatedPreviousEndDay = moment(
            nextSeasonAfterRemoved.startDay,
            "DD-MM-YYYY",
            true,
          )
            .subtract(1, "days")
            .format("DD-MM-YYYY");

          updatedSeasonsList[previousSeasonIndex] = {
            ...updatedSeasonsList[previousSeasonIndex],
            endDay: updatedPreviousEndDay,
          };
        }
      }

      if (
        removedSeasonIndex === prevSeasonsList.length - 1 &&
        updatedSeasonsList.length > 0
      ) {
        const newLastIndex = updatedSeasonsList.length - 1;
        const newLastSeason = updatedSeasonsList[newLastIndex];
        const endOfYearForSeason = moment(
          newLastSeason?.startDay,
          "DD-MM-YYYY",
          true,
        );

        if (endOfYearForSeason.isValid()) {
          updatedSeasonsList[newLastIndex] = {
            ...newLastSeason,
            endDay: endOfYearForSeason.endOf("year").format("DD-MM-YYYY"),
          };
        }
      }

      if (updatedSeasonsList.length > 0) {
        const first = updatedSeasonsList[0];
        const [d1, m1, y1] = first.startDay.split("-");
        const firstStartDate = new Date(`${y1}-${m1}-${d1}`);
        const targetEndDate = moment(firstStartDate).add(1, "years").subtract(1, "days").toDate();
        const pad = (n) => n.toString().padStart(2, "0");
        const endYear = targetEndDate.getFullYear();
        const endMonth = pad(targetEndDate.getMonth() + 1);
        const endDay = pad(targetEndDate.getDate());
        const lastIndex = updatedSeasonsList.length - 1;
        updatedSeasonsList[lastIndex] = {
          ...updatedSeasonsList[lastIndex],
          endDay: `${endDay}-${endMonth}-${endYear}`,
        };
      }
      const newActiveSeasonIndex =
        updatedSeasonsList.length > 0 ? updatedSeasonsList.length - 1 : 0;
      setActiveSeason(newActiveSeasonIndex);
      const nextSeason = updatedSeasonsList[newActiveSeasonIndex] || null;
      setSelectedSeason(nextSeason);
      syncBaseChargeFromSeason(nextSeason);
      return updatedSeasonsList;
    });
    props?.onChange?.();
    
    setShowConfirmRemoveSeason(false);
    setSeasonToRemove(null);
  };

  const handleDoubleClick = (index, item) => {
    if (isReadOnly) return;
    setEditingIndex(index);
    setNewItemValue(item.name);
  };

  const handleInputChange = (e) => {
    if (isReadOnly) return;
    props?.onChange?.();
    setNewItemValue(e.target.value);
  };

  const handleInputBlur = (index) => {
    if (isReadOnly) return;
    const updatedSeasonsList = [...seasonsList];
    updatedSeasonsList[index].name = newItemValue;
    setSeasonsList(updatedSeasonsList);
    setEditingIndex(null);
  };

  const handleDateChange = (date) => {
    if (isReadOnly) return;
    if (isFirstSeason()) return; // Prevent any changes to Season 1's date
    props?.onChange?.();

    const pad = (n) => n.toString().padStart(2, "0");
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const formattedDate = `${day}-${month}-${year}`;

    // Only for non-first seasons: Calculate previous season's endDay as one day before new startDay
    let prevSeasonEndDate = moment(`${year}-${month}-${day}`, "YYYY-MM-DD").subtract(1, "days").toDate();
    const prevEndYear = prevSeasonEndDate.getFullYear();
    const prevEndMonth = pad(prevSeasonEndDate.getMonth() + 1);
    const prevEndDay = pad(prevSeasonEndDate.getDate());
    const updatedSeason = { ...selectedSeason, startDay: formattedDate };

    const updatedSeasonsList = seasonsList.map((season) =>
      season.seasonId === updatedSeason.seasonId ? updatedSeason : season
    );

    if (updatedSeasonsList.length > 1) {
      const previousSeasonIndex =
        updatedSeasonsList.findIndex(
          (season) => season.seasonId === updatedSeason.seasonId
        ) - 1;

      if (previousSeasonIndex >= 0) {
        updatedSeasonsList[previousSeasonIndex].endDay = `${prevEndDay}-${prevEndMonth}-${prevEndYear}`;
      }
    }

    setSeasonsList(updatedSeasonsList);
    setSelectedSeason(updatedSeason);
  };

  const handleDayGroupClick = (index) => {
    setActiveDayGroup(index);
  };

  const handleDayGroupDoubleClick = (index, item) => {
    if (isReadOnly) return;
    setEditingDayGroupIndex(index);
    setNewDayGroupValue(item.dayGroupName);
  };

  const handleDayGroupInputChange = (e) => {
    if (isReadOnly) return;
    setNewDayGroupValue(e.target.value);
    props?.onChange?.();
  };

  const handleDayGroupInputBlur = (index) => {
    if (isReadOnly) return;
    const updatedDayGroups = [...selectedSeason.periods];
    updatedDayGroups[index].dayGroupName = newDayGroupValue;

    const updatedSeason = { ...selectedSeason, periods: updatedDayGroups };
    const updatedSeasonsList = seasonsList.map((season) =>
      season.seasonId === updatedSeason.seasonId ? updatedSeason : season
    );

    setSeasonsList(updatedSeasonsList);
    setSelectedSeason(updatedSeason);
    setEditingDayGroupIndex(null);
  };

  const addDayGroup = () => {
    if (isReadOnly) return;
    const usedWeekdays = new Set();
    selectedSeason.periods.forEach((dayGroup) => {
      dayGroup.weekDays.forEach((day) => usedWeekdays.add(day));
    });

    const allWeekdays = [1, 2, 3, 4, 5, 6, 7]; // Assuming weekdays are represented by numbers 1-7
    const unusedWeekdays = allWeekdays.filter((day) => !usedWeekdays.has(day));

    if (unusedWeekdays.length === 0) {
      console.warn("No unused weekdays available to add a new day group.");
      callSnackbar(
        "error",
        "No unused weekdays available to add a new day group."
      );
      return;
    }

    const newDayGroup = {
      dayGroupId: getRandomInt(1, 9999),
      dayGroupName: `WeekEnd`,
      rate: [
        {
          tariffId: getRandomInt(1, 9999),
          name: "PERIOD 1",
          demandType: "MonthlyMax_US",
          demandCost: 0,
          minPowerValue: 0,
          maxPowerValue: "Infinity",
          timeSlots: [],
          options: {
            tierType: "Off",
            tierBoundaries: [
              {
                tierId: getRandomInt(1, 9999),
                price: defaultEnergyPrice,
                consumption: 0,
              },
            ],
          },
        },
      ],
      weekDays: unusedWeekdays, // Assign the first unused weekday
    };

    const updatedDayGroups = [...selectedSeason.periods, newDayGroup];
    const updatedSeason = { ...selectedSeason, periods: updatedDayGroups };
    const updatedSeasonsList = seasonsList.map((season) =>
      season.seasonId === updatedSeason.seasonId ? updatedSeason : season
    );

    setSeasonsList(updatedSeasonsList);
    setSelectedSeason(updatedSeason);
    setActiveDayGroup(updatedDayGroups.length - 1);
    props?.onChange?.();
  };

  const removeDayGroup = (dayGroupId) => {
    if (isReadOnly) return;
    setDayGroupToRemove(dayGroupId);
    setShowConfirmRemoveDayGroup(true);
  };

  const confirmRemoveDayGroup = () => {
    if (!dayGroupToRemove) return;

    const updatedDayGroups = selectedSeason.periods.filter(
      (dayGroup) => dayGroup.dayGroupId !== dayGroupToRemove
    );
    const updatedSeason = { ...selectedSeason, periods: updatedDayGroups };
    const updatedSeasonsList = seasonsList.map((season) =>
      season.seasonId === updatedSeason.seasonId ? updatedSeason : season
    );

    setSeasonsList(updatedSeasonsList);
    setSelectedSeason(updatedSeason);
    setActiveDayGroup(updatedDayGroups.length > 0 ? 0 : null);

    setShowConfirmRemoveDayGroup(false);
    setDayGroupToRemove(null);
    props?.onChange?.();
  };

  const handleWeekdayClick = (dayId) => {
    if (isReadOnly) return;
    // Prevent overlap of weekdays between periods in inactive seasons
    const allPeriods = selectedSeason.periods;
    // Collect all used weekdays except the current active group
    const usedWeekdays = new Set();
    allPeriods.forEach((period, idx) => {
      if (idx !== activeDayGroup) {
        period.weekDays.forEach((day) => usedWeekdays.add(day));
      }
    });
    if (usedWeekdays.has(dayId)) {
      callSnackbar(
        "error",
        t("tariff.error.weekdayAlreadyAssigned")
      );
      return;
    }
    const prevSelectedSeason = selectedSeason;
    const updatedDayGroup = [...prevSelectedSeason.periods];
    const weekDays = updatedDayGroup[activeDayGroup].weekDays;
    
    if (weekDays.includes(dayId)) {
      // Remove weekday directly without confirmation
      const newWeekDays = weekDays.filter((id) => id !== dayId);
      
      if (newWeekDays.length === 0) {
        callSnackbar(
          "error",
          t("tariff.error.atLeastOneWeekday")
        );
        return;
      }
      
      updatedDayGroup[activeDayGroup].weekDays = newWeekDays;
      const updatedSeason = { ...prevSelectedSeason, periods: updatedDayGroup };
      setSelectedSeason(updatedSeason);
      setSeasonsList((sl) => sl.map((s) => (s.seasonId === updatedSeason.seasonId ? updatedSeason : s)));
      props?.onChange?.();
      return;
    }
    
    // Add weekday
    const newWeekDays = [...weekDays, dayId];
    updatedDayGroup[activeDayGroup].weekDays = newWeekDays;
    const updatedSeason = { ...prevSelectedSeason, periods: updatedDayGroup };
    setSelectedSeason(updatedSeason);
    setSeasonsList((sl) => sl.map((s) => (s.seasonId === updatedSeason.seasonId ? updatedSeason : s)));
    props?.onChange?.();
  };



  const addPeriodToDayGroup = () => {
    if (isReadOnly) return;
    // Check if the active day group's time slots already cover 24 hours
    const periods = selectedSeason?.periods?.[activeDayGroup]?.rate || [];
    if (periods.length > 0) {
      // Calculate total minutes covered by all time slots across all periods
      const totalMinutesAllPeriods = periods.reduce((sum, period) => {
        const slots = period.timeSlots || [];
        const periodMinutes = slots.reduce((s, slot) => {
          const start = slot.startTime.hour * 60 + slot.startTime.minute;
          const end = slot.endTime.hour * 60 + slot.endTime.minute;
          return s + (end - start);
        }, 0);
        return sum + periodMinutes;
      }, 0);
      props?.onChange?.();
      if (totalMinutesAllPeriods === 24 * 60) {
        callSnackbar(
          "error",
          t("tariff.error.cannotAddPeriod24h")
        );
        return;
      }
      // Additionally, ensure the last period has at least one time slot before allowing another
      const lastPeriod = periods[periods.length - 1];
      const timeSlots = lastPeriod.timeSlots || [];
      if (timeSlots.length === 0) {
        callSnackbar(
          "error",
          t("tariff.error.addTimeSlotLastPeriod")
        );
        return;
      }
    }
    const prevSelectedSeason = selectedSeason;
    const updatedDayGroup = [...prevSelectedSeason.periods];
    const activeGroup = updatedDayGroup[activeDayGroup];
    const lastPeriod = activeGroup.rate[activeGroup.rate.length - 1];
    const newPeriod = {
      tariffId: getRandomInt(1, 9999),
      name: `PERIOD ${activeGroup?.rate?.length + 1}`,
      timeSlots: [],
      options: {
        tierType: "Off",
        tierBoundaries: lastPeriod
          ? activeGroup?.rate?.[0]?.options?.tierBoundaries.map((tier) => {
              const newTier = {
                tierId: getRandomInt(1, 9999),
                price: defaultEnergyPrice,
                consumption: 0,
              };
              // Propagate requirement flags and associated numeric fields
              if (tier.hasOwnProperty("fixedRequired") && tier.fixedRequired === true) {
                newTier.fixedRequired = true;
                newTier.fixed = 0;
              } else if (tier.hasOwnProperty("fixed")) {
                // Legacy case
                newTier.fixed = 0;
              }
              if (tier.hasOwnProperty("fractionRequired") && tier.fractionRequired === true) {
                newTier.fractionRequired = true;
                newTier.fraction = 0;
              } else if (tier.hasOwnProperty("fraction")) {
                newTier.fraction = 0;
              }
              if (tier.hasOwnProperty("penalty")) newTier.penalty = 0;
              if (tier.hasOwnProperty("minimumBilling")) newTier.minimumBilling = 0;
              return newTier;
            })
          : [
              {
                tierId: getRandomInt(1, 9999),
                price: defaultEnergyPrice,
                consumption: 0,
              },
            ],
      },
    };
    activeGroup.rate = [...activeGroup.rate, newPeriod];
    const updatedSeason = { ...prevSelectedSeason, periods: updatedDayGroup };
    setSelectedSeason(updatedSeason);
    setSeasonsList((sl) => sl.map((s) => (s.seasonId === updatedSeason.seasonId ? updatedSeason : s)));
    setCurrentDate(new Date());
  };

  const addTierToDayGroup = () => {
    if (isReadOnly) return;
    props?.onChange?.();
    // Limit to 8 tiers max
    const prevSelectedSeason = selectedSeason;
    const updatedDayGroup = [...prevSelectedSeason.periods];
    const activeGroup = updatedDayGroup[activeDayGroup];
    // Check all periods in the (cloned) active group for tier count
    const hasTooManyTiers = activeGroup.rate.some(
      (period) => period.options.tierBoundaries.length >= 30
    );
    // Uncomment to enforce max tiers rule
    if (hasTooManyTiers) {
      callSnackbar(
        "error",
        t("tariff.error.max30Tiers")
      );
      return;
    }
    const newTier = {
      tierId: getRandomInt(1, 9999),
      price: defaultEnergyPrice,
      consumption: 0,
    };
    activeGroup.rate = activeGroup.rate.map((period) => ({
      ...period,
      options: {
        tierType: "Off",
        tierBoundaries: [...period.options.tierBoundaries, newTier],
      },
    }));
    const updatedSeason = { ...prevSelectedSeason, periods: updatedDayGroup };
    setSelectedSeason(updatedSeason);
    setSeasonsList((sl) => sl.map((s) => (s.seasonId === updatedSeason.seasonId ? updatedSeason : s)));
    setCurrentDate(new Date());
  };

  const removePeriodFromDayGroup = () => {
    if (isReadOnly) return;
    props?.onChange?.();
    const prevSelectedSeason = selectedSeason;
    const updatedDayGroup = [...prevSelectedSeason.periods];
    const activeGroup = updatedDayGroup[activeDayGroup];
    if (activeGroup.rate.length === 1) {
      callSnackbar(
        "error",
        t("tariff.error.cannotRemoveLastPeriod")
      );
      return;
    }
    activeGroup.rate.pop();
    const updatedSeason = { ...prevSelectedSeason, periods: updatedDayGroup };
    setSelectedSeason(updatedSeason);
    setSeasonsList((sl) => sl.map((s) => (s.seasonId === updatedSeason.seasonId ? updatedSeason : s)));
    setCurrentDate(new Date());
  };

  const removeTierfromDayGroup = () => {
    if (isReadOnly) return;
    props?.onChange?.();
    const prevSelectedSeason = selectedSeason;
    const updatedDayGroup = [...prevSelectedSeason.periods];
    const activeGroup = updatedDayGroup[activeDayGroup];
    let blocked = false;
    activeGroup.rate.forEach((period) => {
      if (period.options.tierBoundaries.length === 1) {
        blocked = true;
        return;
      }
      period.options.tierBoundaries.pop();
    });
    if (blocked) {
      callSnackbar(
        "error",
        t("tariff.error.cannotRemoveLastTier")
      );
      return;
    }
    const updatedSeason = { ...prevSelectedSeason, periods: updatedDayGroup };
    setSelectedSeason(updatedSeason);
    setSeasonsList((sl) => sl.map((s) => (s.seasonId === updatedSeason.seasonId ? updatedSeason : s)));
    setCurrentDate(new Date());
  };

  const updateTierInDayGroup = (
    periodIndex,
    tierIndex,
    newPrice,
    newConsumption
  ) => {
    if (isReadOnly) return;
    props?.onChange?.();
    const prevSelectedSeason = selectedSeason;
    const updatedDayGroup = [...prevSelectedSeason.periods];
    const activeGroup = updatedDayGroup[activeDayGroup];
    activeGroup.rate[periodIndex].options.tierBoundaries = activeGroup.rate[
      periodIndex
    ].options.tierBoundaries.map((tier, index) =>
      index === tierIndex ? { ...tier, price: newPrice, consumption: newConsumption } : tier
    );
    const updatedSeason = { ...prevSelectedSeason, periods: updatedDayGroup };
    setSelectedSeason(updatedSeason);
    setSeasonsList((sl) => sl.map((s) => (s.seasonId === updatedSeason.seasonId ? updatedSeason : s)));
    setCurrentDate(new Date());
  };

  const updateTierFractionInDayGroup = (
    periodIndex,
    tierIndex,
    newPrice,
    newConsumption,
    newFixed,
    newFraction,
    newPenalty,
    newminimumBilling
  ) => {
    if (isReadOnly) return;
    props?.onChange?.();
    const prevSelectedSeason = selectedSeason;
    const updatedDayGroup = [...prevSelectedSeason.periods];
    const activeGroup = updatedDayGroup[activeDayGroup];
    activeGroup.rate[periodIndex].options.tierBoundaries = activeGroup.rate[
      periodIndex
    ].options.tierBoundaries.map((tier, index) => {
      if (index === tierIndex) {
        const updatedTier = { ...tier };
        if (typeof newPrice !== "undefined") updatedTier.price = newPrice;
        if (typeof newConsumption !== "undefined") updatedTier.consumption = newConsumption;
        if (typeof newFixed !== "undefined") updatedTier.fixed = newFixed;
        if (typeof newFraction !== "undefined") updatedTier.fraction = newFraction;
        if (typeof newPenalty !== "undefined") updatedTier.penalty = newPenalty;
        if (typeof newminimumBilling !== "undefined") updatedTier.minimumBilling = newminimumBilling;
        return updatedTier;
      }
      return tier;
    });
    const updatedSeason = { ...prevSelectedSeason, periods: updatedDayGroup };
    setSelectedSeason(updatedSeason);
    setSeasonsList((sl) => sl.map((s) => (s.seasonId === updatedSeason.seasonId ? updatedSeason : s)));
    setCurrentDate(new Date());
  };

  const updateTimeSlot = (periodIndex, newStartTime, newEndTime) => {
    if (isReadOnly) return;
    props?.onChange?.();
    const prevSelectedSeason = selectedSeason;
    const updatedDayGroup = [...prevSelectedSeason.periods];
    const activeGroup = updatedDayGroup[activeDayGroup];
    activeGroup.rate[periodIndex].timeSlots.push({ startTime: newStartTime, endTime: newEndTime });
    const updatedSeason = { ...prevSelectedSeason, periods: updatedDayGroup };
    setSelectedSeason(updatedSeason);
    setSeasonsList((sl) => sl.map((s) => (s.seasonId === updatedSeason.seasonId ? updatedSeason : s)));
  };

  const updatePeriodName = (periodIndex, newPeriodName) => {
    if (isReadOnly) return;
    props?.onChange?.();
    const prevSelectedSeason = selectedSeason;
    const updatedDayGroup = [...prevSelectedSeason.periods];
    const activeGroup = updatedDayGroup[activeDayGroup];
    activeGroup.rate[periodIndex].name = newPeriodName;
    const updatedSeason = { ...prevSelectedSeason, periods: updatedDayGroup };
    setSelectedSeason(updatedSeason);
    setSeasonsList((sl) => sl.map((s) => (s.seasonId === updatedSeason.seasonId ? updatedSeason : s)));
  };

  const copyRatesFromSeason = (sourceSeasonId) => {
    if (!sourceSeasonId || !selectedSeason) return;
    const sourceSeason = seasonsList.find((s) => String(s.seasonId) === String(sourceSeasonId));
    if (!sourceSeason) return;
    // Deep-clone day groups from source, preserving weekDays + dayGroupName, assigning new IDs
    const updatedPeriods = sourceSeason.periods.map((sourceDg) => ({
      ...JSON.parse(JSON.stringify(sourceDg)),
      dayGroupId: getRandomInt(1, 9999),
      rate: sourceDg.rate.map((period) => ({
        ...JSON.parse(JSON.stringify(period)),
        tariffId: getRandomInt(1, 9999),
        options: {
          ...period.options,
          tierBoundaries: (period.options?.tierBoundaries || []).map((tier) => ({
            ...tier,
            tierId: getRandomInt(1, 9999),
          })),
        },
      })),
    }));
    const updatedSeason = { ...selectedSeason, periods: updatedPeriods };
    setSelectedSeason(updatedSeason);
    setActiveDayGroup(0);
    setSeasonsList((sl) => sl.map((s) => (s.seasonId === updatedSeason.seasonId ? updatedSeason : s)));
    setCurrentDate(new Date());
    callSnackbar("success", "Day groups and rates copied successfully");
  };

  const usedWeekdays = getUsedWeekdays(selectedSeason, activeDayGroup);

  const handleStartTimeChange = (value) => {
    if (isReadOnly) return;
    if (!value) return;
    const [hour, minute] = value.split(":").map(Number);
    setSelectedSeason((prevSeason) => ({
      ...prevSeason,
      seasonStartTime: { hour, minute },
    }));
  };

  // Dynamically compute date constraints per season:
  // For a given selected season:
  // minDate = previous season startDay + 1 day (if previous exists)
  // maxDate = next season startDay - 1 day (if next exists) else global year window end
  const dateConstraints = (() => {
    if (!selectedSeason || seasonsList.length === 0) return {};
    const index = seasonsList.findIndex(s => s.seasonId === selectedSeason.seasonId);
    if (index === -1) return {};
    
    // Get the first season's start date as the base for the 1-year window
    const firstSeason = seasonsList[0];
    const [fd, fm, fy] = firstSeason.startDay.split('-');
    const firstSeasonStart = new Date(`${fy}-${fm}-${fd}`);
    
    // Calculate the 1-year window end (1 year from first season start - 1 day)
    const globalEnd = moment(firstSeasonStart).add(1, 'years').subtract(1, 'days').toDate();
    
    // For the first season, no minimum date constraint
    let minDate = null;
    
    // For non-first seasons, must start after previous season's end
    if (index > 0) {
      const prev = seasonsList[index - 1];
      if (prev?.startDay) {
        const [pd, pm, py] = prev.startDay.split('-');
        const prevStart = new Date(`${py}-${pm}-${pd}`);
        minDate = moment(prevStart).add(1, 'days').toDate();
      }
    }
    
    // Maximum date is constrained by: next season's start (if exists) OR the 1-year window end
    let maxDate = globalEnd;
    if (index < seasonsList.length - 1) {
      const next = seasonsList[index + 1];
      if (next?.startDay) {
        const [nd, nm, ny] = next.startDay.split('-');
        const nextStart = new Date(`${ny}-${nm}-${nd}`);
        const nextMaxDate = moment(nextStart).subtract(1, 'days').toDate();
        // Use the earlier of the two constraints
        maxDate = moment(nextMaxDate).isBefore(globalEnd) ? nextMaxDate : globalEnd;
      }
    }
    
    return { minDate, maxDate };
  })();

  const removeTimeSlotIndex = (tariff, tariffId, timeSlotIndex) => {
    if (tariff.tariffId === tariffId) {
      // Remove the timeSlot at the specified timeSlotIndex
      const updatedTimeSlots = tariff?.timeSlots?.filter(
        (_, index) => index !== timeSlotIndex
      );
      return { ...tariff, timeSlots: updatedTimeSlots };
    }
    return tariff;
  };

  const handleRemoveTimeSlots = (timeSlotIndex, tariffId) => {
    setSelectedSeason((prevSelectedSeason) => {
      if (isReadOnly) return prevSelectedSeason;
      props?.onChange?.();
      if (!prevSelectedSeason) return prevSelectedSeason;
      const updatedDayGroup = [...prevSelectedSeason.periods];
      const activeGroup = updatedDayGroup[activeDayGroup];
      activeGroup.rate = activeGroup?.rate?.map((tariff) =>
        removeTimeSlotIndex(tariff, tariffId, timeSlotIndex)
      );
      const updatedSeason = { ...prevSelectedSeason, periods: updatedDayGroup };
      setSeasonsList((prev) =>
        prev.map((s) => (s.seasonId === updatedSeason.seasonId ? updatedSeason : s))
      );
      setComponentStatus((prev) => ({
        ...prev,
        grid: { isEditing: true, completed: false },
      }));
      return updatedSeason;
    });
  };

  const selectedSeasonDayGroupUpdate = (rate, tierIndex, key, checked) => {
    if (isReadOnly) return rate;
    props?.onChange?.();
    // Only update the tier at tierIndex in this rate's tierBoundaries
    const updatedTierBoundaries = rate?.options?.tierBoundaries.map(
      (tier, idx) => {
        if (idx === tierIndex) {
          const updatedTier = { ...tier };
          // Normalise potential typo in key
          const normalisedKey = key === 'fixedRequiered' ? 'fixedRequired' : key;
          // Handle new boolean requirement flags for fixed/fraction visibility
          if (normalisedKey === 'fixedRequired') {
            if (checked) {
              updatedTier.fixedRequired = true;
              if (!updatedTier.hasOwnProperty('fixed')) updatedTier.fixed = 0;
            } else {
              delete updatedTier.fixedRequired;
              // Remove numeric value when requirement disabled
              delete updatedTier.fixed;
            }
            return updatedTier;
          }
          if (normalisedKey === 'fractionRequired') {
            if (checked) {
              updatedTier.fractionRequired = true;
              if (!updatedTier.hasOwnProperty('fraction')) updatedTier.fraction = 0;
            } else {
              delete updatedTier.fractionRequired;
              delete updatedTier.fraction;
            }
            return updatedTier;
          }
          // Existing numeric extras (penalty/minimumBilling) retain prior behavior
          if (checked) {
            updatedTier[normalisedKey] = 0;
          } else {
            delete updatedTier[normalisedKey];
          }
          return updatedTier;
        }
        return tier;
      }
    );
    return {
      ...rate,
      options: { ...rate.options, tierBoundaries: updatedTierBoundaries },
    };
  };

  const updateSelectedSeasonAndDayGroup = (tierIndex, key, checked) => {
    if (isReadOnly) return;
    props?.onChange?.();
    const prevSelectedSeason = selectedSeason;
    const updatedPeriods = [...prevSelectedSeason.periods];
    const activeGroup = updatedPeriods[activeDayGroup];
    activeGroup.rate = activeGroup?.rate?.map((rate) => selectedSeasonDayGroupUpdate(rate, tierIndex, key, checked));
    const updatedSeason = { ...prevSelectedSeason, periods: updatedPeriods };
    setSelectedSeason(updatedSeason);
    setSeasonsList((sl) => sl.map((s) => (s.seasonId === updatedSeason.seasonId ? updatedSeason : s)));
  };

  const { type, message } = snackbarProps;
  const { colorScheme } = useMantineColorScheme();

  const grayIconStyle = useMemo(
    () => ({
      padding: "4px",
    }),
    [colorScheme],
  ); 

  return (
    <>
      <se-snackbar
        id={snackbarIdRef.current}
        type={type}
        icon="information_stroke"
        message={message}
        duration="3000"
      ></se-snackbar>
      <div>
        <DynamicTabbar
          items={seasonsList}
          activeIndex={activeSeason}
          tabName={activeSeason !== null ? seasonsList[activeSeason]?.name : ""}
          editingIndex={editingIndex}
          newItemValue={newItemValue}
          handleItemClick={handleSeasonClick}
          handleDoubleClick={handleDoubleClick}
          handleInputChange={handleInputChange}
          handleInputBlur={handleInputBlur}
          removeItem={removeSeason}
          addItem={addSeason}
          itemKey="seasonId"
          itemName="name"
          itemId="seasonId"
          showAddRemove={!props.isVersionCompleted}
        />
        <div className="mb-2">
          <div className="row">
            <div className="d-flex flex-column col-sm-8 col-md-8">
              <div
                className="row"
                style={{ marginTop: "10px", marginBottom: "10px" }}
              >
                <div className="col-sm-12 col-md-12 col-xl-12 d-flex">
                  {" "}
                  {/* <span className="header-content-roman">I. </span> */}
                  <span className="header-content">
                    {" "}
                    {t("tariff.header.defineYearlySeason")}
                  </span>
                </div>
              </div>

              <div className="row">
                <div className="col-sm-12 col-md-12 col-lg-6 col-xl-6 ">
                    <se-form-field
                    // padding="none"
                    // label-align="left"
                    // label-width="30%"
                    type="date"
                    label={t("tariff.label.startDate")}
                    required={true}
                    option="stacked"
                  >
                    <DateInput
                      {...props?.getFieldAttributes("field-18")}
                      data-id="field-18"
                      clearable
                      onChange={handleDateChange}
                      placeholder={t("tariff.placeholder.dateInput")}
                      valueFormat={projectDateFormat}
                      required
                      // disabled={isReadOnly || isFirstSeason()}
                      readOnly={isReadOnly || isFirstSeason()}
                      value={(() => {
                        const dateStr = selectedSeason?.startDay || "";
                        // Robust parsing to avoid timezone shifts across regions
                        // Use moment to parse and then construct a local Date (year, month-1, day)
                        // so the same calendar date is shown regardless of user timezone.
                        if (!dateStr) return null;
                        // Try ISO first via moment
                        if (/^\d{4}-\d{2}-\d{2}(T.*)?$/.test(dateStr)) {
                          const m = moment(dateStr, moment.ISO_8601, true);
                          if (m.isValid()) {
                            const y = m.year();
                            const mIdx = m.month(); // 0-based
                            const dNum = m.date();
                            const local = new Date(y, mIdx, dNum);
                            return isNaN(local.getTime()) ? null : local;
                          }
                        }
                        // Fallback: dd-mm-yyyy
                        const parts = dateStr.split("-");
                        if (parts.length === 3) {
                          const [day, month, year] = parts;
                          const m = moment(`${day}-${month}-${year}`, "DD-MM-YYYY", true);
                          if (m.isValid()) {
                            const y = m.year();
                            const mIdx = m.month();
                            const dNum = m.date();
                            const local = new Date(y, mIdx, dNum);
                            return isNaN(local.getTime()) ? null : local;
                          }
                        }
                        return null;
                      })()}
                      rightSection={
                        <div style={grayIconStyle}>
                          <IconCalendar size="1.1rem" stroke={1.5} />
                        </div>
                      }
                      rightSectionProps={{ style: { pointerEvents: "none" } }}
                      {...(() => {
                        // First season logic:
                        // - If there are other seasons (length > 1), Season 1 is locked
                        // - If Season 1 is alone, allow selection of past dates
                        //   BUT constrain maxDate so that (startDate + 1 year) <= today
                        //   This ensures all seasons fit within past/present, not extending to future
                        if (isFirstSeason()) {
                          const today = new Date();
                          // Only allow editing Season 1 date if it's the only season
                          if (seasonsList.length === 1) {
                            // maxDate = today - 1 year, so (selected date + 1 year) = today
                            const maxDate = moment(today).subtract(1, 'years').toDate();
                            return {
                              minDate: new Date(1900, 0, 1), // Allow any date from past
                              maxDate // Ensures 1-year season stays within present
                            };
                          }
                          // If other seasons exist, Season 1 is locked (no date changes allowed)
                          return {};
                        }
                        // Non-first seasons: use existing dateConstraints
                        const { minDate, maxDate } = dateConstraints;
                        const constraints = {};
                        if (minDate) constraints.minDate = minDate;
                        if (maxDate) constraints.maxDate = maxDate;
                        return constraints;
                      })()}
                      disabled={isReadOnly || isFirstSeason()}
                      popoverProps={{
                        withinPortal: true,
                        position: "bottom-start",
                        disabled: isReadOnly || isFirstSeason(),
                        // styles: {
                        //   dropdown: { height: "400px" }
                        // }
                      }}
                    />
                  </se-form-field>
                </div>
                {!isReadOnly && seasonsList.length > 1 && (
                  <div className="col-sm-12 col-md-12 col-lg-6 col-xl-6 d-flex align-items-end pb-1" style={{ gap: 12, flexWrap: "wrap" }}>
                    <Checkbox
                      checked={copyFromEnabled}
                      onChange={(e) => {
                        const checked = e.currentTarget.checked;
                        setCopyFromEnabled(checked);
                        if (!checked) setCopyFromSeasonId(null);
                      }}
                      label={<span style={{ fontWeight: 600, fontSize: 13 }}>Copy From</span>}
                      size="md"
                      color="green"
                    />
                    {copyFromEnabled && (
                      <Select
                        placeholder="Select season to copy from"
                        data={seasonsList
                          .filter((s) => s.seasonId !== selectedSeason?.seasonId)
                          .map((s) => ({ value: String(s.seasonId), label: s.name }))}
                        value={copyFromSeasonId}
                        onChange={(val) => {
                          if (val) {
                            setPendingCopySeasonId(val);
                            setShowCopyConfirmModal(true);
                          }
                        }}
                        style={{ minWidth: 200 }}
                        withinPortal
                      />
                    )}
                  </div>
                )}
                {/*
                    <se-form-field
                    option="stacked"
                    type="select"
                    label={t("tariff.label.startTime")}
                    required={true}
                  >
                    <Select
                      data={timeOptions}
                      value={
                        formatTime(
                          selectedSeason?.seasonStartTime?.hour,
                          selectedSeason?.seasonStartTime?.minute
                        ) || null
                      }
                      onChange={handleStartTimeChange}
                      disabled={isReadOnly}
                      // classNames={{
                      //   input:
                      //     colorScheme === "dark"
                      //       ? "custom-time-dark-input"
                      //       : "custom-time-input",
                      // }}
                      // styles={{ root: { width: "100%" } }}
                    />
                  </se-form-field>
                </div> */}
              </div>
                <div className="col-sm-12 col-md-12 col-xl-12 d-flex mb-3 mt-3">
                  {" "}
                  {/* <span className="header-content-roman">II. </span> */}
                  <span className="header-content">
                    {t("tariff.header.defineWeekdays")}
                  </span>
                </div>
                <div className="col-sm-12 col-md-12 col-xl-12">
                  <DynamicTabbar
                    items={selectedSeason?.periods || []}
                    activeIndex={activeDayGroup}
                    editingIndex={editingDayGroupIndex}
                    newItemValue={newDayGroupValue}
                    handleItemClick={handleDayGroupClick}
                    handleDoubleClick={handleDayGroupDoubleClick}
                    handleInputChange={handleDayGroupInputChange}
                    handleInputBlur={handleDayGroupInputBlur}
                    removeItem={removeDayGroup}
                    addItem={addDayGroup}
                    itemKey="dayGroupId"
                    itemName="dayGroupName"
                    itemId="dayGroupId"
                    showAddRemove={!props.isVersionCompleted}
                  />
                  <WeekdaysSelector
                    WEEK_DAYS={WEEK_DAYS}
                    usedWeekdays={usedWeekdays}
                    selectedSeason={selectedSeason}
                    activeDayGroup={activeDayGroup}
                    handleWeekdayClick={handleWeekdayClick}
                  />
                </div>
                
                <div className="col-sm-12 col-md-12 col-xl-12">
                  <TimeSlotsDisplay
                    selectedSeason={selectedSeason}
                    activeDayGroup={activeDayGroup}
                  />
                </div>
              </div>
               {/* <div style={{ marginBottom: "16px", display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ display: "flex", alignItems: "center", minHeight: 40 }}>
                    <Checkbox
                    label={t("tariff.labels.baseCharge")}
                    checked={isBaseCharge}
                    onChange={(e) => {
                      if (isReadOnly) return;
                      const checked = e.currentTarget.checked;
                      setIsBaseCharge(checked);
                      setSeasonsList((prev) =>
                        prev.map((season, idx) =>
                          idx === activeSeason ? { ...season, isBaseCharge: checked } : season
                        )
                      );
                      setSelectedSeason((prev) => (prev ? { ...prev, isBaseCharge: checked } : prev));
                    }}
                    size="md"
                    style={{ marginRight: 12 }}
                    disabled={isReadOnly}
                  />
                  <div
                    style={{
                      width: 130,
                      transition: "opacity 0.2s",
                      opacity: isBaseCharge ? 1 : 0,
                      pointerEvents: isBaseCharge ? "auto" : "none",
                    }}
                  >
                    <TextInput
                      value={isBaseCharge ? baseCharge : ""}
                      onChange={(e) => {
                        if (isReadOnly) return;
                        const raw = e.target.value;
                        if (raw === "" || DECIMAL_INPUT_REGEX.test(raw)) {
                          setBaseCharge(raw);
                          setSeasonsList((prev) =>
                            prev.map((season, idx) =>
                              idx === activeSeason ? { ...season, baseCharge: raw } : season
                            )
                          );
                          setSelectedSeason((prev) => (prev ? { ...prev, baseCharge: raw } : prev));
                        }
                      }}
                      size="md"
                      placeholder={t("tariff.placeholder.enterBaseCharge")}
                      inputMode="decimal"
                      styles={{ input: { paddingLeft: 10, paddingRight: 10 } }}
                      disabled={isReadOnly}
                    />
                  </div>
                </div>
              </div> */}
              <div className="row">
                <div className="col-sm-12 col-md-12 col-xl-12 d-flex mb-3">
                  {" "}
                  {/* <span className="header-content-roman">III. </span> */}
                    <span className="header-content">
                    {t("tariff.header.defineTariffPeriod")}
                </span>
                </div>
              </div>
              {!isReadOnly && <div className="row">
                <div className="col-sm-12 col-md-12 col-xl-12 d-flex mb-3">
                  <Group>
                    <Button
                      aria-label="tou-tariff-add-period"
                      onClick={addPeriodToDayGroup}
                      style={{ backgroundColor: "#42b4e6" }}
                      disabled={isReadOnly}
                    >
                      <SeIcon>electricity_polarity_positive_plus</SeIcon>
                      {t("tariff.button.addPeriod")}
                    </Button>
                    <Button aria-label="tou-tariff-remove-period" color={"gray"} onClick={removePeriodFromDayGroup} disabled={isReadOnly}>
                      <SeIcon>electricity_polarity_negative_minus</SeIcon>
                      {t("tariff.button.removePeriod")}
                    </Button>
                    <Button
                      aria-label="tou-tariff-add-tier"
                      onClick={addTierToDayGroup}
                      style={{ backgroundColor: "#42b4e6" }}
                      disabled={isReadOnly}
                    >
                      <SeIcon>electricity_polarity_positive_plus</SeIcon>
                      {t("tariff.button.addTier")}
                    </Button>
                    <Button aria-label="tou-tariff-remove-tier" color={"gray"} onClick={removeTierfromDayGroup} disabled={isReadOnly}>
                      <SeIcon>electricity_polarity_negative_minus</SeIcon>
                      {t("tariff.button.removeTier")}
                    </Button>
                  </Group>
                </div>
              </div>}
            </div>
          </div>

          <div>
            {selectedSeason?.periods[activeDayGroup] && (
              <TariffDataSheet
                activeDayGroup={selectedSeason?.periods[activeDayGroup]}
                updateTier={updateTierInDayGroup}
                updatePeriodName={updatePeriodName}
                selectedSeason={selectedSeason}
                activeDayGroupIndex={activeDayGroup}
                updateTimeSlot={updateTimeSlot}
                callSnackbar={callSnackbar}
                handleRemoveTimeSlots={handleRemoveTimeSlots}
                currentDate={currentDate}
                updateTierFractionInDayGroup={updateTierFractionInDayGroup}
                updateSelectedSeasonAndDayGroup={
                  updateSelectedSeasonAndDayGroup
                }
                allowBill={props.allowBill} // pass down to child
                allowPenalty={props.allowPenalty} // pass down to child
                  isReadOnly={isReadOnly}
              />
            )}
          </div>
        </div>

      {/* Confirmation Modal for Removing Season */}
      <SEConfirmModal
        opened={showConfirmRemoveSeason}
        onClose={() => {
          setShowConfirmRemoveSeason(false);
          setSeasonToRemove(null);
        }}
        onConfirm={confirmRemoveSeason}
        header={t("tariff.modal.confirmRemoveSeason", { defaultValue: "Alert" })}
        message={t("tariff.modal.removeSeasonMessage", { defaultValue: "Are you sure you want to delete? This action cannot be undone." })}
        cancelLabel={t("common.cancel", { defaultValue: "Cancel" })}
        confirmLabel={t("common.delete", { defaultValue: "Delete" })}
      />

      {/* Confirmation Modal for Copy From */}
      <SEConfirmModal
        opened={showCopyConfirmModal}
        onClose={() => {
          setShowCopyConfirmModal(false);
          setPendingCopySeasonId(null);
          setCopyFromSeasonId(null);
          setCopyFromEnabled(false);
        }}
        onConfirm={() => {
          if (pendingCopySeasonId) {
            copyRatesFromSeason(pendingCopySeasonId);
          }
          setShowCopyConfirmModal(false);
          setPendingCopySeasonId(null);
          setCopyFromSeasonId(null);
          setCopyFromEnabled(false);
        }}
        header={t("tariff.modal.copyDayGroupData", { defaultValue: "Alert" })}
        message={(() => {
          const src = seasonsList.find((s) => String(s.seasonId) === String(pendingCopySeasonId));
          return `Do you want to copy all day group data and rates from ${src?.name ?? "the selected season"} to ${selectedSeason?.name ?? "current season"}? This will replace existing data.`;
        })()}
        cancelLabel={t("common.cancel", { defaultValue: "Cancel" })}
        confirmLabel={t("common.copy", { defaultValue: "Copy" })}
      />

      {/* Confirmation Modal for Removing Day Group */}
      <SEConfirmModal
        opened={showConfirmRemoveDayGroup}
        onClose={() => {
          setShowConfirmRemoveDayGroup(false);
          setDayGroupToRemove(null);
        }}
        onConfirm={confirmRemoveDayGroup}
        header={t("tariff.modal.confirmRemoveDayGroup", { defaultValue: "Alert" })}
        message={t("tariff.modal.removeDayGroupMessage", { defaultValue: "Are you sure you want to delete? This action cannot be undone." })}
        cancelLabel={t("common.cancel", { defaultValue: "Cancel" })}
        confirmLabel={t("common.delete", { defaultValue: "Delete" })}
      />

    </>
  );
});

MGTariff.propTypes = {
  tariffTemplate: PropTypes.array,
};
